// src/components/StoryProgress.jsx
import React, { useMemo, useRef } from 'react';
import '../css/StoryProgress.css';

export default function StoryProgress({ steps, currentIndex, onStepClick }) {
  const clamped = Math.max(0, Math.min(currentIndex, steps.length - 1));

  const prevIndexRef = useRef(clamped);
  const prevIndex = prevIndexRef.current;
  const delta = clamped - prevIndex; // >0 forward, <0 backward, 0 none
  const isForward = delta > 0;
  const isBackward = delta < 0;

  const animateIndex = isForward ? clamped : isBackward ? prevIndex : -1;
  prevIndexRef.current = clamped;

  const animKey = useMemo(
    () => `${animateIndex}-${isForward ? 'fwd' : isBackward ? 'back' : 'none'}`,
    [animateIndex, isForward, isBackward]
  );

  const handleKey = (e, i) => {
    if (!onStepClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onStepClick(i);
    }
  };

  return (
    <div
      className="story-track"
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={steps.length}
      aria-valuenow={clamped + 1}
      aria-label={`Step ${clamped + 1} of ${steps.length}`}
    >
      {steps.map((step, i) => {
        const isCompleted = i < clamped;
        const isActive = i === clamped;
        const shouldAnimateForward = isForward && i === animateIndex;
        const shouldAnimateBackward = isBackward && i === animateIndex;

        let fillClasses = 'story-fill';
        if (isCompleted) fillClasses += ' w-100';
        if (shouldAnimateForward) fillClasses += ' anim-fill-forward';
        if (shouldAnimateBackward) fillClasses += ' anim-fill-backward';

        return (
          <div
            className={`story-segment${isActive ? ' is-active' : ''}${isCompleted ? ' is-complete' : ''}`}
            key={`${step}-${i}`}
          >
            <button
              type="button"
              className="story-bg story-click"
              aria-current={isActive ? 'step' : undefined}
              aria-label={`Go to ${step} (${i + 1}/${steps.length})`}
              onClick={onStepClick ? () => onStepClick(i) : undefined}
              onKeyDown={(e) => handleKey(e, i)}
              tabIndex={0}
            />
            <div
              className={fillClasses}
              key={i === animateIndex ? animKey : `static-${i}`}
              style={{ pointerEvents: 'none' }}
            />
          </div>
        );
      })}
    </div>
  );
}
